/**
 * markdownDoc.js — shared Markdown → HTML renderer for long-form documents
 * rendered read-only in the portal (staff guides, policies, SOP).
 *
 * Why this exists
 * ---------------
 * The Guides Library reader and the Policy Reader previously dumped raw
 * Markdown into a <pre> block, so staff saw literal "## 3. Opening a New
 * Matter", "| Field | Meaning |" and "**Important**" instead of formatted
 * documents. SOPPage had its own regex renderer that could not handle nested
 * lists, fenced code, or numbered items written as "1.Text" (which the staff
 * guides use). This module is the single render path for all three surfaces.
 *
 * Guarantees
 * ----------
 *  - Input is HTML-escaped before any markup is emitted, so document text can
 *    never inject tags. The renderer only emits its own known tag set
 *    (sanitisation by construction); callers additionally run DOMPurify.
 *  - Link targets are restricted to http(s), mailto and site-relative paths.
 *  - Block support: ATX headings (h1-h6), GFM tables with alignment, unordered
 *    and ordered lists with indentation-based nesting, blockquotes, fenced and
 *    indented code, horizontal rules, paragraphs.
 *  - Inline support: code spans, links, bold, italic, strikethrough.
 *  - Tolerances observed in real Bonsai content: "1.Text" (no space after the
 *    number), "*" / "+" bullets, setext-free headings with trailing hashes,
 *    tables whose rows are missing the outer pipes.
 */

const CODE_SPAN_TOKEN = "\u0000CODESPAN";

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function safeHref(raw) {
  const url = String(raw || "").trim();
  if (/^(https?:\/\/|mailto:|tel:)/i.test(url)) return url;
  if (/^[/#]/.test(url)) return url;
  return null;
}

/** Inline markup. Input is raw (unescaped) Markdown for a single logical line. */
export function renderInline(text) {
  if (text == null) return "";

  // 1. Pull out code spans before anything else so their contents are literal.
  const spans = [];
  let s = String(text).replace(/`([^`]+)`/g, (_m, code) => {
    spans.push(code);
    return `${CODE_SPAN_TOKEN}${spans.length - 1}\u0000`;
  });

  // 2. Escape everything else.
  s = escapeHtml(s);

  // 3. Links and bare autolinks.
  s = s.replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, (m, alt, url) => {
    const href = safeHref(url);
    return href ? `<img src="${href}" alt="${alt}" />` : alt;
  });
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, (m, label, url) => {
    const href = safeHref(url);
    if (!href) return label;
    const ext = /^https?:/i.test(href);
    return `<a href="${href}"${ext ? ' target="_blank" rel="noopener noreferrer"' : ""}>${label}</a>`;
  });
  s = s.replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, (m, pre, url) =>
    `${pre}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
  );

  // 4. Emphasis. Bold before italic so "**x**" is not eaten by the italic rule.
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  s = s.replace(/(^|[^*\w])\*([^*\n]+)\*(?![*\w])/g, "$1<em>$2</em>");
  s = s.replace(/~~([^~]+)~~/g, "<del>$1</del>");

  // 5. Restore code spans, escaped.
  s = s.replace(new RegExp(`${CODE_SPAN_TOKEN}(\\d+)\\u0000`, "g"), (_m, i) =>
    `<code>${escapeHtml(spans[Number(i)])}</code>`
  );

  return s;
}

function isTableRow(line) {
  return /\|/.test(line) && /^\|?[^|]*\|/.test(line.trim());
}

function isTableSeparator(line) {
  const t = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  if (!t.includes("-")) return false;
  return t.split("|").every((c) => /^\s*:?-{1,}:?\s*$/.test(c));
}

function splitRow(line) {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|\s*$/, "")
    .split("|")
    .map((c) => c.trim());
}

function alignmentsFrom(sepLine) {
  return splitRow(sepLine).map((c) => {
    const left = c.startsWith(":");
    const right = c.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    if (left) return "left";
    return null;
  });
}

function listMarker(line) {
  const bullet = line.match(/^(\s*)([-*+])\s+(.*)$/);
  if (bullet) {
    return { type: "ul", indent: bullet[1].replace(/\t/g, "  ").length, text: bullet[3] };
  }
  // Ordered items, tolerating "1.Text" with no space after the delimiter.
  const ordered = line.match(/^(\s*)(\d{1,3})[.)](\s+|(?=\S))(.*)$/);
  if (ordered) {
    return { type: "ol", indent: ordered[1].replace(/\t/g, "  ").length, text: ordered[4] };
  }
  return null;
}

export function renderMarkdownDoc(md) {
  if (!md) return "";

  const lines = String(md).replace(/\r\n?/g, "\n").split("\n");
  const out = [];
  let i = 0;

  // Open list stack: entries are { type, indent }
  let stack = [];
  let para = [];

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${renderInline(para.join("\n")).replace(/\n/g, "<br/>")}</p>`);
      para = [];
    }
  };
  const closeLists = (toIndent = -1) => {
    while (stack.length && (toIndent < 0 || stack[stack.length - 1].indent > toIndent)) {
      out.push(`</li></${stack.pop().type}>`);
    }
  };
  const closeBlocks = () => {
    flushPara();
    closeLists();
  };

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw.replace(/\s+$/, "");
    const trimmed = line.trim();

    // Blank line — ends a paragraph, keeps any open list open so that
    // blank-line-separated bullets stay one list.
    if (!trimmed) {
      flushPara();
      i += 1;
      continue;
    }

    // Fenced code block.
    const fence = trimmed.match(/^(```|~~~)(.*)$/);
    if (fence) {
      closeBlocks();
      const marker = fence[1];
      const body = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith(marker)) {
        body.push(lines[i]);
        i += 1;
      }
      i += 1; // consume closing fence (or run off the end)
      out.push(`<pre><code>${escapeHtml(body.join("\n"))}</code></pre>`);
      continue;
    }

    // Horizontal rule.
    if (/^(\s*)([-*_])(\s*\2){2,}\s*$/.test(line)) {
      closeBlocks();
      out.push("<hr/>");
      i += 1;
      continue;
    }

    // ATX heading.
    const heading = trimmed.match(/^(#{1,6})\s*(.+?)\s*#*\s*$/);
    if (heading) {
      closeBlocks();
      const level = heading[1].length;
      out.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      i += 1;
      continue;
    }

    // Table: a row followed by an alignment separator.
    if (isTableRow(trimmed) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      closeBlocks();
      const headers = splitRow(trimmed);
      const aligns = alignmentsFrom(lines[i + 1]);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim() && isTableRow(lines[i].trim())) {
        rows.push(splitRow(lines[i].trim()));
        i += 1;
      }
      const th = headers
        .map((h, c) => {
          const a = aligns[c] ? ` style="text-align:${aligns[c]}"` : "";
          return `<th${a}>${renderInline(h)}</th>`;
        })
        .join("");
      const tb = rows
        .map((r) => {
          const cells = headers
            .map((_h, c) => {
              const a = aligns[c] ? ` style="text-align:${aligns[c]}"` : "";
              return `<td${a}>${renderInline(r[c] == null ? "" : r[c])}</td>`;
            })
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");
      out.push(`<table><thead><tr>${th}</tr></thead><tbody>${tb}</tbody></table>`);
      continue;
    }

    // Blockquote — gather the run, render its contents recursively.
    if (/^\s*>/.test(line)) {
      closeBlocks();
      const body = [];
      while (i < lines.length && (/^\s*>/.test(lines[i]) || (lines[i].trim() && body.length && !listMarker(lines[i])))) {
        body.push(lines[i].replace(/^\s*>\s?/, ""));
        i += 1;
      }
      out.push(`<blockquote>${renderMarkdownDoc(body.join("\n"))}</blockquote>`);
      continue;
    }

    // Lists, with indentation-based nesting.
    const item = listMarker(line);
    if (item) {
      flushPara();
      const top = stack[stack.length - 1];
      if (!top) {
        out.push(`<${item.type}><li>`);
        stack.push({ type: item.type, indent: item.indent });
      } else if (item.indent > top.indent + 1) {
        out.push(`<${item.type}><li>`);
        stack.push({ type: item.type, indent: item.indent });
      } else {
        closeLists(item.indent);
        const now = stack[stack.length - 1];
        if (!now) {
          out.push(`<${item.type}><li>`);
          stack.push({ type: item.type, indent: item.indent });
        } else if (now.type !== item.type) {
          out.push(`</li></${stack.pop().type}>`);
          out.push(`<${item.type}><li>`);
          stack.push({ type: item.type, indent: item.indent });
        } else {
          out.push("</li><li>");
        }
      }
      out.push(renderInline(item.text));
      i += 1;
      continue;
    }

    // Continuation line inside an open list item.
    if (stack.length && /^\s{2,}\S/.test(line)) {
      out.push(` ${renderInline(trimmed)}`);
      i += 1;
      continue;
    }

    // Plain paragraph text — closes any open list.
    if (stack.length) closeLists();
    para.push(trimmed);
    i += 1;
  }

  closeBlocks();
  return out.join("\n");
}

export default renderMarkdownDoc;
